import type {CopilotChatMode, CopilotChatModel, TurnUsage} from '@github-ui/copilot-chat-types'
import {calculateTurnCredits, normalizeTokenPrices} from '@github-ui/copilot-shared-ui/utils/model-pricing'
import {sendEvent} from '@github-ui/hydro-analytics'
import {InfoIcon, MeterIcon} from '@primer/octicons-react'
import {AnchoredOverlay, IconButton, Stack, Text, Tooltip} from '@primer/react'
import {memo, useState} from 'react'

import {useChatStateValues} from '../utils/CopilotChatContext'
import styles from './MessageTokenUsage.module.css'

type TokenUsageDisplay = Pick<TurnUsage, 'inputTokens' | 'outputTokens' | 'cacheReadTokens'>

interface MessageTokenUsageWithDataProps {
  usage: TokenUsageDisplay
  modelId?: string
  availableModels?: ModelForTokenUsage[]
  mode?: CopilotChatMode
  variant?: 'usage' | 'info'
}

export type ModelForTokenUsage = {
  id: string
  name?: string
  displayName?: string
  billing?: CopilotChatModel['billing']
  tokenPrices?: {
    inputPrice: number
    outputPrice: number
    cachePrice: number
  }
}

export const MessageTokenUsagePopover = memo(function MessageTokenUsagePopoverInner({
  usage,
  totalCredits,
  onOpen,
  modelName,
  variant = 'usage',
}: {
  usage: TokenUsageDisplay
  totalCredits?: number | null
  onOpen?: () => void
  modelName?: string
  variant?: 'usage' | 'info'
}) {
  const [isOpen, setIsOpen] = useState(false)
  const creditsLabel =
    totalCredits !== null && totalCredits !== undefined
      ? `${totalCredits.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} AI ${totalCredits === 1 ? 'credit' : 'credits'}`
      : undefined
  const modelLabel = modelName ?? 'Model unavailable'

  return (
    <AnchoredOverlay
      open={isOpen}
      onOpen={() => {
        setIsOpen(true)
        onOpen?.()
      }}
      onClose={() => setIsOpen(false)}
      renderAnchor={props =>
        variant === 'info' ? (
          <Tooltip text={`${modelLabel} · ${creditsLabel ?? 'AI Credits cost unavailable'}`} type="description">
            <IconButton icon={InfoIcon} aria-label="View message info" variant="invisible" size="small" {...props} />
          </Tooltip>
        ) : (
          <IconButton
            icon={MeterIcon}
            aria-label="View message token usage"
            variant="invisible"
            size="small"
            {...props}
          />
        )
      }
      side="outside-top"
      align="center"
      width="auto"
    >
      <Stack direction="vertical" gap="condensed" padding="normal" className={styles.popover}>
        <Text size="small" weight="semibold">
          {variant === 'info' ? 'Message info' : 'Message token usage'}
        </Text>
        <div className={styles.grid}>
          {variant === 'info' && (
            <>
              <Text size="small" className={styles.label}>
                Model:
              </Text>
              <Text size="small" className={styles.value}>
                {modelLabel}
              </Text>
            </>
          )}
          <Text size="small" className={styles.label}>
            Input:
          </Text>
          <Text size="small" className={styles.value}>
            {Math.round(usage.inputTokens).toLocaleString()} tokens
          </Text>
          <Text size="small" className={styles.label}>
            Output:
          </Text>
          <Text size="small" className={styles.value}>
            {Math.round(usage.outputTokens).toLocaleString()} tokens
          </Text>
          {totalCredits !== null && totalCredits !== undefined && (
            <>
              <Text size="small" className={styles.label}>
                Total:
              </Text>
              <Text size="small" className={styles.value}>
                {creditsLabel}
              </Text>
            </>
          )}
        </div>
      </Stack>
    </AnchoredOverlay>
  )
})

export const MessageTokenUsage = memo(function MessageTokenUsageInner({
  usage,
  modelId,
}: {
  usage: TurnUsage
  modelId?: string
}) {
  const {availableModels, mode} = useChatStateValues('availableModels', 'mode')

  return <MessageTokenUsageWithData usage={usage} modelId={modelId} availableModels={availableModels} mode={mode} />
})

export const MessageTokenUsageWithData = memo(function MessageTokenUsageWithDataInner({
  usage,
  modelId,
  availableModels,
  mode,
  variant,
}: MessageTokenUsageWithDataProps) {
  const model = availableModels?.find?.(m => m.id === modelId)
  const pricing = normalizeTokenPrices(model?.billing?.token_prices)
  const totalCredits = model?.tokenPrices
    ? calculateTurnCredits(usage, {defaultTier: model.tokenPrices})
    : pricing
      ? calculateTurnCredits(usage, pricing)
      : null

  return (
    <MessageTokenUsagePopover
      usage={usage}
      totalCredits={totalCredits}
      modelName={model?.displayName ?? model?.name ?? modelId}
      variant={variant}
      onOpen={() => {
        if (!mode) return
        sendEvent('copilot_chat.activate', {
          target: 'MESSAGE_TOKEN_USAGE',
          action: 'opened',
          mode,
          model: modelId,
          hasCredits: totalCredits !== null,
        })
      }}
    />
  )
})
